import { loadBrandLogo } from '@/lib/brandedQr'

/**
 * Turns a tenant's `receipt_logo_url` into something Electron can actually use
 * as a window/taskbar icon.
 *
 * The desktop shell already had the whole plumbing for this — an IPC channel,
 * a main-process handler, a renderer caller — and it still showed the generic
 * Bhookly icon on every tenant. Two reasons, both silent:
 *
 *  1. `nativeImage.createFromBuffer` cannot decode SVG. The handler checks
 *     `img.isEmpty()` and returns without a word, so an SVG logo produced
 *     exactly the same observable behaviour as no logo at all.
 *  2. A receipt logo is typically a WIDE banner. Handing that straight to
 *     `setIcon` gives a squashed, letterboxed icon even when the format works.
 *
 * Both are fixed here rather than in the main process, because the renderer has
 * a canvas: Chromium will happily draw an SVG image element onto one, so
 * rasterising here normalises every input format to a square PNG. The main
 * process then only ever has to decode a PNG, which it can always do.
 */

/** Windows wants 256px for the large icon; everything smaller is downscaled from it. */
export const APP_ICON_SIZE_PX = 256

/**
 * Contain-fit: scale to fit inside the square without cropping or distorting,
 * then centre. Exported so the arithmetic is testable without a real canvas —
 * getting this wrong is how you end up with a stretched logo, and that is not
 * something a unit test can see through a mocked 2D context.
 */
export function containFit(
  srcW: number,
  srcH: number,
  box: number,
): { w: number; h: number; x: number; y: number } {
  if (!(srcW > 0) || !(srcH > 0)) return { w: box, h: box, x: 0, y: 0 }
  const scale = Math.min(box / srcW, box / srcH)
  const w = Math.max(1, Math.round(srcW * scale))
  const h = Math.max(1, Math.round(srcH * scale))
  return { w, h, x: Math.round((box - w) / 2), y: Math.round((box - h) / 2) }
}

function decodeImage(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    // Already a data: URL from loadBrandLogo, so no crossOrigin dance and no
    // tainted canvas — which matters because we call toDataURL() below.
    img.src = dataUrl
  })
}

/**
 * Resolve a logo URL to a square PNG data URL, or null if it cannot be used.
 *
 * Returning null is a normal outcome, not an error: a tenant with no logo, an
 * unreachable URL, or a file that is not an image all land here, and the caller
 * simply leaves the baked icon alone.
 */
export async function buildDesktopAppIconPng(
  logoUrl: string | null | undefined,
  size: number = APP_ICON_SIZE_PX,
): Promise<string | null> {
  const dataUrl = await loadBrandLogo(logoUrl)
  if (!dataUrl) return null

  const img = await decodeImage(dataUrl)
  if (!img) return null

  // SVGs can report 0 here when they carry no intrinsic size; fall back to the
  // target box so they rasterise at full bleed instead of collapsing to 1px.
  const srcW = img.naturalWidth || img.width || size
  const srcH = img.naturalHeight || img.height || size

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // Transparent background on purpose. A white plate would look like a sticker
  // on a dark taskbar, and Windows composites the alpha correctly.
  ctx.clearRect(0, 0, size, size)
  const { w, h, x, y } = containFit(srcW, srcH, size)
  ctx.drawImage(img, x, y, w, h)

  try {
    return canvas.toDataURL('image/png')
  } catch {
    // Tainted canvas — only reachable if loadBrandLogo ever stops inlining.
    return null
  }
}
